import type { AppSettings, AnalyticsConsent } from '../types/settings';

export type ConsentAction = 'enable' | 'disable' | 'none';

export interface ConsentTransitionResult {
  next: AppSettings;
  action: ConsentAction;
}

export function applyConsentChange(
  current: AppSettings,
  to: AnalyticsConsent,
): ConsentTransitionResult {
  if (current.analyticsConsent === to) {
    return { next: current, action: 'none' };
  }
  if (to === 'granted' || to === 'auto-granted') {
    return {
      next: {
        ...current,
        analyticsConsent: to,
        analyticsAnonId: crypto.randomUUID(),
        analyticsInstallSalt: crypto.randomUUID(),
      },
      action: 'enable',
    };
  }
  return {
    next: { ...current, analyticsConsent: to },
    action: 'disable',
  };
}

/**
 * True for any opt-in state ('granted' or 'auto-granted'). Use this everywhere
 * code needs to decide whether analytics SDKs should run; checking
 * `=== 'granted'` directly would miss the auto-opt-in path.
 */
export function isOptedIn(state: AnalyticsConsent | null): boolean {
  return state === 'granted' || state === 'auto-granted';
}

// GPC always wins: a 'pending' user with GPC is treated as declined (no
// banner shown, no SDKs loaded), and an 'auto-granted' user with GPC is
// also treated as declined defensively — the auto-opt-in flow checks GPC
// before granting, but if state was written elsewhere we still respect GPC.
// Explicit grants/declines are preserved; the user opted in per-site.
export function effectiveConsent(stored: AnalyticsConsent): AnalyticsConsent {
  if (stored === 'granted' || stored === 'declined') return stored;
  if (typeof navigator !== 'undefined' && navigator.globalPrivacyControl === true) {
    return 'declined';
  }
  return stored;
}

export async function patternAnonId(salt: string, patternId: string): Promise<string> {
  const data = new TextEncoder().encode(`${salt}:${patternId}`);
  const digest = await crypto.subtle.digest('SHA-256', data);
  const hex = Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  return hex.slice(0, 12);
}
